import React from "react";

import { useProposalDocument } from "./proposalDocumentsContext";

type PdfQuality = "screen" | "ebook" | "print";

interface PrintQualityOption {
  value: PdfQuality;
  label: string;
  hint: string;
}

interface ColorModeOption {
  value: boolean;
  label: string;
  hint: string;
}

const PRINT_QUALITY_OPTIONS: PrintQualityOption[] = [
  // NOTE: Commented out - there if we need it but we don't want people to use this and send it to clients
  // {
  //   value: "screen",
  //   label: "Poor",
  //   hint: "Poorest quality, lowest file size. Internal use only. NOT suitable for sending to clients.",
  // },
  {
    value: "ebook",
    label: "Standard",
    hint: "Balanced quality and file size. Use this for sending proposals via email.",
  },
  {
    value: "print",
    label: "High",
    hint: "Best quality for physical media. Use this when you want to print a physical proposal brochure.",
  },
];

const COLOR_MODE_OPTIONS: ColorModeOption[] = [
  {
    value: false,
    label: "Exact Colors",
    hint: "Use best available colours. Use this for digital documents.",
  },
  {
    value: true,
    label: "Economy",
    hint: "Optimised for printing. Use this for physical documents.",
  },
];

const FormatSelector: React.FC = () => {
  const { payload, setPayload, advancedPrintOptionsEnabled, setAdvancedPrintOptionsEnabled } =
    useProposalDocument();

  const isPdfSelected = payload.format === "pdf";
  const showPrintOptions = isPdfSelected && advancedPrintOptionsEnabled;

  const handleFormatChange = (format: string) => {
    setPayload({ ...payload, format });
  };

  const handlePrintQualityChange = (quality: PdfQuality) => {
    setPayload({ ...payload, pdf_quality: quality });
  };

  const handleColorModeChange = (useFriendlyColors: boolean) => {
    setPayload({ ...payload, use_print_friendly_colours: useFriendlyColors });
  };

  return (
    <div className="format-selector">
      <div className="format-selector__header">
        <h2>Output Format</h2>
      </div>

      <div className="format-selector__content">
        {/* Format Selection */}
        <div className="format-selector__options">
          <label
            className={`format-selector__radio-card ${
              payload.format === "html" ? "format-selector__radio-card--selected" : ""
            }`}
          >
            <input
              type="radio"
              name="proposalFormat"
              value="html"
              checked={payload.format === "html"}
              onChange={(e) => {
                handleFormatChange(e.target.value);
              }}
              className="format-selector__radio-input"
              aria-label="HTML Preview format"
            />
            <span className="format-selector__radio-text">HTML Preview</span>
          </label>

          <label
            className={`format-selector__radio-card ${
              payload.format === "pdf" ? "format-selector__radio-card--selected" : ""
            }`}
          >
            <input
              type="radio"
              name="proposalFormat"
              value="pdf"
              checked={payload.format === "pdf"}
              onChange={(e) => {
                handleFormatChange(e.target.value);
              }}
              className="format-selector__radio-input"
              aria-label="PDF Document format"
            />
            <span className="format-selector__radio-text">PDF Document</span>
          </label>
        </div>

        {/* Advanced Print Options Toggle (PDF only) */}
        {isPdfSelected && (
          <div className="format-selector__print-toggle">
            <label className="format-selector__toggle-label">
              <div className="format-selector__switch">
                <input
                  type="checkbox"
                  checked={advancedPrintOptionsEnabled}
                  onChange={(e) => {
                    setAdvancedPrintOptionsEnabled(e.target.checked);
                  }}
                  aria-label="Enable advanced print options"
                />
                <div className="toggle-track" />
                <div className="toggle-thumb" />
              </div>
              <span>Advanced Print Options</span>
            </label>
          </div>
        )}

        {/* Print Options (PDF only, when advanced enabled) */}
        {showPrintOptions && (
          <div className="format-selector__print-options">
            <h3>Print Settings</h3>

            {/* Print Quality */}
            <div className="format-selector__option-group">
              <span id="format-selector-pdf-quality" className="format-selector__option-label">
                PDF Quality
              </span>
              <div
                className="format-selector__radio-group"
                role="radiogroup"
                aria-labelledby="format-selector-pdf-quality"
              >
                {PRINT_QUALITY_OPTIONS.map((option) => (
                  <label key={option.value} className="format-selector__radio-item">
                    <input
                      type="radio"
                      name="printQuality"
                      value={option.value}
                      checked={payload.pdf_quality === option.value}
                      onChange={() => {
                        handlePrintQualityChange(option.value);
                      }}
                      aria-label={`${option.label} quality`}
                    />
                    <div className="format-selector__radio-content">
                      <span className="radio-label">{option.label}</span>
                      <span className="radio-hint">{option.hint}</span>
                    </div>
                  </label>
                ))}
              </div>
            </div>

            {/* Color Mode */}
            <div className="format-selector__option-group">
              <span id="format-selector-color-mode" className="format-selector__option-label">
                Color Mode
              </span>
              <div
                className="format-selector__radio-group"
                role="radiogroup"
                aria-labelledby="format-selector-color-mode"
              >
                {COLOR_MODE_OPTIONS.map((option) => (
                  <label
                    key={option.value ? "economy" : "exact"}
                    className="format-selector__radio-item"
                  >
                    <input
                      type="radio"
                      name="colorMode"
                      value={option.value ? "economy" : "exact"}
                      checked={payload.use_print_friendly_colours === option.value}
                      onChange={() => {
                        handleColorModeChange(option.value);
                      }}
                      aria-label={`${option.label} color mode`}
                    />
                    <div className="format-selector__radio-content">
                      <span className="radio-label">{option.label}</span>
                      <span className="radio-hint">{option.hint}</span>
                    </div>
                  </label>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default FormatSelector;
